import { I18n } from '@affine/i18n';
import {
  ActionPlacement,
  type ToolbarAction,
  type ToolbarContext,
} from '@blocksuite/affine/shared/services';
import type { GfxModel } from '@blocksuite/affine/std/gfx';
import {
  EdgelessCRUDIdentifier,
  updateXYWH,
} from '@blocksuite/affine-block-surface';
import { Bound } from '@blocksuite/global/gfx';
import { AutoTidyUpIcon } from '@blocksuite/icons/lit';
import type { FrameworkProvider } from '@toeverything/infra';

import {
  type BlockRegistryState,
  type EdgeDefinition,
  EdgeDerivationService,
  type IBlockRegistry,
  ReactiveEdgeService,
  type RegisteredAutomation,
  type RegisteredDatabase,
  type RegisteredGhost,
  type StreamlainEdge,
} from '../edge-infrastructure';
import {
  computeStreamLainEdgelessLayout,
  type StreamLainEdgelessLayoutEdge,
  type StreamLainEdgelessLayoutKind,
  type StreamLainEdgelessLayoutNode,
} from './layout-engine';

type GfxModelWithProps = GfxModel & {
  flavour?: string;
  model?: {
    flavour?: string;
    props?: Record<string, unknown>;
  };
  props?: Record<string, unknown>;
  xywh: string;
};

type LayoutSelectionNode = StreamLainEdgelessLayoutNode & {
  readonly model: GfxModel;
};

export function createStreamLainEdgelessLayoutToolbarAction(
  framework: FrameworkProvider
): ToolbarAction {
  const label = I18n.t(
    'com.streamlain.edgeless.toolbar.arrangeAutomationDatabaseBlocks'
  );

  return {
    placement: ActionPlacement.Start,
    id: 'B.streamlain-edgeless-layout',
    label,
    showLabel: true,
    tooltip: label,
    icon: AutoTidyUpIcon(),
    when: ctx =>
      ctx.isEdgelessMode &&
      getSelectedStreamLainLayoutModelCount(ctx.getSurfaceModels()) >= 2,
    run: ctx => {
      applyStreamLainEdgelessSelectionLayout(ctx, framework);
    },
  };
}

export function getSelectedStreamLainLayoutModelCount(
  models: readonly GfxModel[]
): number {
  return models.filter(isStreamLainLayoutModel).length;
}

export function applyStreamLainEdgelessSelectionLayout(
  ctx: ToolbarContext,
  framework: FrameworkProvider
): void {
  const selectedModels = ctx.getSurfaceModels();
  const selectedIds = new Set(
    selectedModels.filter(isStreamLainLayoutModel).map(model => model.id)
  );
  if (selectedIds.size < 2) return;

  const edgeService = framework.getOptional(ReactiveEdgeService);
  const registry = edgeService?.getBlockRegistry() ?? null;
  registry?.forceRescan();

  const state = registry
    ? {
        databases: registry.getDatabases(),
        automations: registry.getAutomations(),
        elsters: registry.getElsters(),
        fileSources: registry.getFileSources(),
        ghosts: registry.getGhosts(),
      }
    : null;

  const nodes = collectLayoutNodes(selectedModels, selectedIds, state);
  if (nodes.length < 2) return;

  const nodeIds = new Set(nodes.map(node => node.elementId));
  const edges = collectLayoutEdges(edgeService, registry).filter(
    edge => nodeIds.has(edge.sourceId) && nodeIds.has(edge.targetId)
  );

  const positions = computeStreamLainEdgelessLayout(nodes, edges);
  const modelByElementId = new Map(nodes.map(node => [node.elementId, node]));
  const { updateElement } = ctx.std.get(EdgelessCRUDIdentifier);

  for (const position of positions) {
    const node = modelByElementId.get(position.elementId);
    if (!node) continue;

    const model = node.model as GfxModelWithProps;
    const bound = Bound.deserialize(model.xywh);
    const elementBound = model.elementBound;
    const offsetX = bound.x - elementBound.x;
    const offsetY = bound.y - elementBound.y;
    bound.x = position.x + offsetX;
    bound.y = position.y + offsetY;

    updateXYWH(model, bound, updateElement, ctx.store.updateBlock);
  }
}

function collectLayoutNodes(
  selectedModels: readonly GfxModel[],
  selectedIds: ReadonlySet<string>,
  state: BlockRegistryState | null
): LayoutSelectionNode[] {
  const selectedModelById = new Map(
    selectedModels.map(model => [model.id, model])
  );
  const nodes = new Map<string, LayoutSelectionNode>();

  if (state) {
    for (const db of state.databases) {
      addRegistryNode(nodes, selectedModelById, selectedIds, db, 'database');
    }
    for (const automation of state.automations) {
      addRegistryNode(
        nodes,
        selectedModelById,
        selectedIds,
        automation,
        'automation'
      );
    }
    for (const ghost of state.ghosts) {
      addRegistryNode(
        nodes,
        selectedModelById,
        selectedIds,
        ghost,
        ghost.ghostType === 'automation' ? 'automation-ghost' : 'database-ghost'
      );
    }
  }

  for (const model of selectedModels) {
    if (!selectedIds.has(model.id) || nodes.has(model.id)) continue;
    const kind = getStreamLainLayoutKind(model);
    if (!kind) continue;
    const bound = model.elementBound;
    nodes.set(model.id, {
      id: model.id,
      elementId: model.id,
      kind,
      x: bound.x,
      y: bound.y,
      width: bound.w,
      height: bound.h,
      model,
    });
  }

  return [...nodes.values()];
}

function addRegistryNode(
  nodes: Map<string, LayoutSelectionNode>,
  selectedModelById: ReadonlyMap<string, GfxModel>,
  selectedIds: ReadonlySet<string>,
  item: RegisteredDatabase | RegisteredAutomation | RegisteredGhost,
  kind: StreamLainEdgelessLayoutKind
): void {
  if (!selectedIds.has(item.elementId) && !selectedIds.has(item.blockId)) {
    return;
  }

  const model =
    selectedModelById.get(item.elementId) ??
    selectedModelById.get(item.blockId);
  if (!model || !isStreamLainLayoutModel(model)) return;

  nodes.set(item.elementId, {
    id: item.blockId,
    elementId: item.elementId,
    kind,
    x: item.bound.x,
    y: item.bound.y,
    width: item.bound.w,
    height: item.bound.h,
    model,
  });
}

function collectLayoutEdges(
  edgeService: ReactiveEdgeService | undefined,
  registry: IBlockRegistry | null
): StreamLainEdgelessLayoutEdge[] {
  const renderedEdges = edgeService?.getRenderingService()?.getAllEdges() ?? [];
  if (renderedEdges.length > 0) {
    return renderedEdges.flatMap(toLayoutEdge);
  }

  if (!registry) return [];

  const derivedEdges = new EdgeDerivationService().deriveRequiredEdges(
    registry.getDatabases(),
    registry.getAutomations(),
    registry.getGhosts(),
    undefined,
    undefined,
    registry.getElsters(),
    registry.getFileSources()
  );
  return derivedEdges.flatMap(toLayoutEdge);
}

function toLayoutEdge(
  edge: StreamlainEdge | EdgeDefinition
): StreamLainEdgelessLayoutEdge[] {
  if (edge.type !== 'automation' && edge.type !== 'relation') return [];
  const metadataKind = (edge.metadata as { kind?: string } | undefined)?.kind;
  if (metadataKind === 'file-index') return [];
  if (edge.sourceId === edge.targetId) return [];

  return [
    {
      sourceId: edge.sourceId,
      targetId: edge.targetId,
      type: edge.type,
    },
  ];
}

function isStreamLainLayoutModel(model: GfxModel): boolean {
  return getStreamLainLayoutKind(model) !== null;
}

function getStreamLainLayoutKind(
  model: GfxModel
): StreamLainEdgelessLayoutKind | null {
  const flavour = getFlavour(model);
  if (flavour === 'affine:database') return 'database';
  if (flavour === 'affine:automation') return 'automation';
  if (flavour !== 'affine:ghost') return null;

  const ghostType = getProps(model).ghostType;
  return ghostType === 'automation' ? 'automation-ghost' : 'database-ghost';
}

function getFlavour(model: GfxModel): string | undefined {
  const candidate = model as GfxModelWithProps;
  return candidate.flavour ?? candidate.model?.flavour;
}

function getProps(model: GfxModel): Record<string, unknown> {
  const candidate = model as GfxModelWithProps;
  return candidate.props ?? candidate.model?.props ?? {};
}
